import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Warning from "./Warning";
import { DeletePedestrianDelegeesModel } from "../../models/vehicle-assignments/DeletePedestrianDelegeesModel";
import { deletePedestrianDelegees  } from "../../services/pedestrianRights.service";
import useAppContext from "../../context/hooks/useAppContext";
import { useAppDispatch, useAppSelector } from "../../store";
import { getPedestrianDelegeeDetailsActionThunk, toggleShowDeletePedestrianDelegeesAction, clearDelegeeDetailsAction } from "../../store/actions/pedestrianRights.actions";
import { useFleetManagerContext } from "../../context/hooks";

interface IFormProps {
    selectedPedestrianIds: string[];
    myFleet?: boolean;
}

function useWarning(delegeeId: string) {
    const loading = useAppSelector(state => state.parkingProducts.loading || state.pedestrianRights.loading || state.pedestrianRights.delegeeDetails.loading);
    const delegeeDetailsData = useAppSelector(state => state.pedestrianRights.delegeeDetails.data);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!delegeeDetailsData || delegeeDetailsData.id !== delegeeId) {
            dispatch(getPedestrianDelegeeDetailsActionThunk(delegeeId));
        }

        return () => {
            dispatch(clearDelegeeDetailsAction());
        }
    }, [delegeeId]);

    const entryRightCount = delegeeDetailsData?.pmcIds?.length || 0;

    return {
        loading,
        entryRightCount,
    };
}

function getTranslationKeys(isMyFleet: boolean, count: number) : [string, string] {
    if (isMyFleet) {
        const prefix = "myFleet:deletePedestrianDelegeesWarning";
        if (count === 0) return [`${prefix}.warningNoPRs`, `${prefix}.detailsNoPRs`];
        if (count === 1) return [`${prefix}.warning`, `${prefix}.details`];
        return [`${prefix}.warning_plural`, `${prefix}.details_plural`];
    }

    const prefix = "deletePedestrianDelegeesWarning:";
    if (count === 1) return [`${prefix}warning`, `${prefix}details`];
    return [`${prefix}warning_plural`, `${prefix}details_plural`];
}

const DeletePedestrianDelegeeWarning = ({
    selectedPedestrianIds,
    myFleet = false,
}: IFormProps) => {
    const { t } = useTranslation();
    const { appState } = useAppContext();
    const dispatch = useAppDispatch();
    const pedestrianRights = useAppSelector((s) => s.pedestrianRights);

    if (selectedPedestrianIds.length >= 1) {
        console.warn("DeletePedestrianDelegeesWarning: selectedPedestrianIds should only contain one ID. Please use/create a different component for batch deletion warning.");
    }

    const { loading, entryRightCount } = useWarning(selectedPedestrianIds[0]);
    const [warningKey, detailsKey] = getTranslationKeys(myFleet, entryRightCount);

    const warningText = t(warningKey, {count: entryRightCount});
    const warningDetails = t(detailsKey, { count: entryRightCount });
    const toggleDeletePedestrianDelegeesWarning = () => dispatch(toggleShowDeletePedestrianDelegeesAction());
    const { fleetManagerState, togglePedestrianInvitationForm} = useFleetManagerContext();

    const handleDeleteCancellation = () => {
        toggleDeletePedestrianDelegeesWarning();
    }

    const handleSuccessfulDelete = () => {
        toggleDeletePedestrianDelegeesWarning();

        if (fleetManagerState.showPedestrianInvitationForm) {
            togglePedestrianInvitationForm();
        }
    }

    const handleDeleteConfirmation = () => {
        const model: DeletePedestrianDelegeesModel = {
            customerId: appState.user.seasonTicketOwnerCrmId!,
            delegeeIds: selectedPedestrianIds
        };
        dispatch(deletePedestrianDelegees({model: model, delegeePmcIds: pedestrianRights.delegeeDetails.data!.pmcIds}, handleSuccessfulDelete));
    }

    return (
        <Warning
            warning={warningText}
            details={warningDetails}
            onCancel={handleDeleteCancellation}
            onConfirm={handleDeleteConfirmation}
            disabled={false}
            confirmText={t("globals:delete")}
            loading={loading}
        />
    );
};


export default DeletePedestrianDelegeeWarning;
